import axios from 'axios';
import { API_URL } from '../config/environment-variables';
import {
  FETCH_COMPARISON_REPORTS,
  FETCH_STANDARD_REPORTS,
  FETCH_TRANSACTION_REPORTS,
  FETCH_TRANSACTION_REPORTS_OFFSET,
  FETCH_TRANSACTION_REPORTS_TOTAL, FETCH_USER_BACKUPS,
  FETCH_USER_INVOICES,
  FETCH_Z_REPORTS,
  REQUEST_RESULT,
} from './types';
import { headerConfig } from './utils';
import {
  DATE_FROM_FIELD,
  DATE_TO_FIELD,
  FIELDS_NEED_TO_BE_CONVERTED,
  TOP_SALES_QUANTITY,
  getConvertedFieldValue,
  TIME_FROM_FIELD, TIME_TO_FIELD,
} from '../components/pages/reports/standard/StandardReportUtils';
import { formatTimestampAsDate } from '../utils/dates-utils';
import FileSaver from 'file-saver';
import { FormattedMessage } from 'react-intl';
import { SALES_PER_CAMPAIGN } from '../components/pages/reports/standard/StandardReportConfig';
import { getMachineProperty } from '../utils/report-utils';
import { analyticEvent } from '../analytics';

export const REPORTS_URL = `${API_URL}/reports`;

export function fetchZReports(machines, values, form) {
  const { machineIDs, dateFrom, dateTo } = values;
  const convertedMachineIDs = machineIDs[0] === null ? machines.map(item => item.value) : machineIDs;
  const [startTime, endTime] = [encodeURIComponent('T00:00:00'), encodeURIComponent('T23:59:59')];
  let transformedResponse = [];
  return dispatch => {
    axios.all(convertedMachineIDs.map(machineID =>
        axios.get(`${REPORTS_URL}/get-z-reports?machineId=${machineID}&startDateTime=${formatTimestampAsDate(dateFrom)}${startTime}&endDateTime=${formatTimestampAsDate(dateTo)}${endTime}`, dispatch(headerConfig()))
          .then(response => {
            transformedResponse = [...transformedResponse, ...response.data.Items.map(item => ({
              ...item,
              machineID: machineID,
            }))];
          }),
      ),
    ).then(() => {
      form.setSubmitting(false);
      dispatch({
        type: FETCH_Z_REPORTS,
        payload: transformedResponse,
      });
    })
      .catch(error => {
          form.setSubmitting(false);
          if (error.response.status === 400) {
            dispatch({
              type: REQUEST_RESULT,
              payload: { message: error.response.data },
            });
          }
        },
      )
    ;
  };
}

export const transformFormValues = (formValues) => {
  return Object.keys(formValues)
    .filter(key => formValues[key] !== undefined && formValues[key] !== null)
    .map(key => {
        const fieldToBeConverted = FIELDS_NEED_TO_BE_CONVERTED.find(field => field.name === key);
        return {
          Name: fieldToBeConverted ? fieldToBeConverted.server : key,
          Value: fieldToBeConverted ? getConvertedFieldValue(fieldToBeConverted.type, formValues[key]) : formValues[key],
        };
      },
    );
};

export function fetchStandardReports(reportName, values, form, setLoadingProgress) {
  const { machineIDs, ...formValues } = values;
  const transformedFormValues = {
    MachineIds: machineIDs[0] === null ? undefined : machineIDs,
    Parameters: transformFormValues(formValues),
  };
  let transformedResponse = [];
  setLoadingProgress(0);
  return dispatch => {
    axios.post(`${REPORTS_URL}/get-report-result/${reportName}`,
      transformedFormValues,
      {
        ...dispatch(headerConfig()),
        onDownloadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
          setLoadingProgress && setLoadingProgress(progress);
        },
      },
    )
      .then(response => {
        transformedResponse = [...transformedResponse, ...response.data.Items.map(item => ({
          ...item,
        }))];
      })
      .then(() => {
          form && form.setSubmitting(false);
          dispatch({
            type: FETCH_STANDARD_REPORTS,
            payload: transformedResponse,
          });
        },
      )
      .catch(error => {
        const errorMessage = error.response.data.match(/<title>(.*?)<\/title>/i)?.[1] || 'Server error';
        form && form.setSubmitting(false);
        dispatch({
          type: REQUEST_RESULT,
          payload: { message: errorMessage },
        });
      });
  };
}

export function fetchComparisonReports(reportName, allMachinesIDs, values, form, setLoadingProgress, machineIDs) {
  const additionalParameters = [{ Name: TIME_FROM_FIELD.server, Value: '00:00:00' }, {
    Name: TIME_TO_FIELD.server,
    Value: '23:59:59',
  }];
  let transformedResponse = [];
  setLoadingProgress(0);
  
  const transformedValues = (formValues) => {
    return {
      MachineIds: machineIDs[0] === null ? undefined : machineIDs,
      Parameters: reportName !== SALES_PER_CAMPAIGN.reportName
        ? [transformFormValues(formValues), ...additionalParameters].flat()
        : transformFormValues(formValues),
    };
  };
  
  return dispatch => {
    axios.all(values.map(dateRange =>
      axios.post(`${REPORTS_URL}/get-report-result/${reportName}`, transformedValues(dateRange),
        {
          ...dispatch(headerConfig()),
          onDownloadProgress: (progressEvent) => {
            const progress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
            setLoadingProgress && setLoadingProgress(progress);
          },
        })
        .then(response => transformedResponse = [...transformedResponse, [response.data.Items.map(item => ({ ...item })), { date: dateRange }]])),
    ).then(() => {
      form?.setSubmitting(false);
      dispatch({
        type: FETCH_COMPARISON_REPORTS,
        payload: transformedResponse,
      });
    })
      .catch(error => {
          form && form.setSubmitting(false);
          dispatch({
            type: REQUEST_RESULT,
            payload: { message: error.response.data },
          });
        },
      );
  };
}

export function fetchTopSalesReports(reportName, allMachinesIDs, values, form) {
  const { machineIDs, ...formValues } = values;
  const SERVER_PARAMS_NAMES = [DATE_FROM_FIELD, DATE_TO_FIELD, TOP_SALES_QUANTITY].map(item => item.server);
  const API_PARAMS = transformFormValues(formValues).filter(item => SERVER_PARAMS_NAMES.includes(item.Name));
  
  const transformedFormValues = {
    MachineIds: machineIDs[0] === null ? undefined : machineIDs,
    Parameters: API_PARAMS,
  };
  
  let transformedResponse = [];
  return dispatch => {
    axios.post(`${REPORTS_URL}/get-report-result/${reportName}`,
      transformedFormValues,
      dispatch(headerConfig()))
      .then(response => {
        transformedResponse = [...response.data.Items.map(item => ({ ...item }))];
      })
      .then(() => {
          form && form.setSubmitting(false);
          dispatch({
            type: FETCH_STANDARD_REPORTS,
            payload: transformedResponse,
          });
        },
      )
      .catch(error => {
          form && form.setSubmitting(false);
          if (error.response.status === 400) {
            dispatch({
              type: REQUEST_RESULT,
              payload: { message: error.response.data },
            });
          }
        },
      );
  };
}

export function downloadZReport(setDownloadProgress, zReportNumber, LogRecordId, machineId, downloadZReportDetails, sie4) {
  const { machines } = downloadZReportDetails;
  const customerName = getMachineProperty(machines, machineId, 'CustomerName');
  const machineName = getMachineProperty(machines, machineId, 'Name');
  const fileExt = sie4 ? 'se' : 'pdf';
  setDownloadProgress(0);
  return dispatch => axios.get(`${REPORTS_URL}/download-z-report/${machineId}/${LogRecordId}${sie4 ? '/sie4' : ''}`,
    {
      ...dispatch(headerConfig()),
      responseType: 'blob',
      onDownloadProgress: (progressEvent) => {
        const progress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
        setDownloadProgress(progress);
      },
    })
    .then(response => {
      const fileName = `Z${zReportNumber}_${machineName}_${customerName}.${fileExt}`;
      const invoiceFile = new File(
        [new Blob([response.data])],
        fileName,
        { type: 'application/pdf' },
      );
      FileSaver.saveAs(invoiceFile);
      analyticEvent('ZReport Download', 'PDF Download', 'Success', { zReportNumber: zReportNumber });
    }).catch((error) => {
      analyticEvent('ZReport Download', 'PDF Download', 'Failure', { zReportNumber: zReportNumber });
      dispatch({
        type: REQUEST_RESULT,
        payload: { message: 'Download error' },
      });
    });
}

export function downloadPeriodicReportsByDate(values, machines, form, setDownloadProgress) {
  const [dateFrom, dateTo, machineIDs] = [formatTimestampAsDate(values.dateFrom), formatTimestampAsDate(values.dateTo), values.machineIDs];
  const formatMachineName = (machineId) => getMachineProperty(machines, machineId, 'Name').replace(/\s+/g, '_');
  const formatCustomerName = (machineId) => getMachineProperty(machines, machineId, 'CustomerName').replace(/\s+/g, '_');
  const machineIDsArray = Array.isArray(machineIDs) ? machineIDs : [machineIDs];
  const machineNames = machineIDsArray.map(formatMachineName).join('_');
  const customerName = formatCustomerName(machineIDsArray[0]);
  const fileName = `periodical_report_${dateFrom}-${dateTo}_${machineNames}_${customerName}.pdf`;
  const [startTime, endTime] = ['T00:00:00', 'T23:59:59'];
  return dispatch => axios.post(`${REPORTS_URL}/download-periodic-report-by-date-multi-machine`,
    {
      startDate: `${dateFrom}${startTime}`,
      endDate: `${dateTo}${endTime}`,
      machineIds: machineIDs,
      forceReportGeneration: true,
    },
    {
      ...dispatch(headerConfig()),
      responseType: 'blob',
      onDownloadProgress: (progressEvent) => {
        const progress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
        setDownloadProgress(progress);
      },
    })
    .then(response => {
      form.setSubmitting(false);
      const invoiceFile = new File([new Blob([response.data])], fileName, { type: 'application/pdf' });
      FileSaver.saveAs(invoiceFile);
      analyticEvent('PeriodicReport Download', 'PDF Download', 'Success', { ID: 'ID' });
    })
    .catch(error => {
      form.setSubmitting(false);
      analyticEvent('PeriodicReport Download', 'PDF Download', 'Failure', { ID: 'ID' });
      if (error.response.status === 400) {
        dispatch({
          type: REQUEST_RESULT,
          payload: {
            message: <FormattedMessage id="page.periodicReport.400.error"
                                       defaultMessage="The report cannot be opened yet.
                                       The information required to open the report is not yet guaranteed to have been transferred from the local cash register to dinkassa.se.
                                       Be sure to select an end date for the report where the transactions have been created and transferred to dinkassa.se.
                                       This means that you cannot specify a date in the future or a date where transactions have not been created." />,
          },
        });
      } else {
        dispatch({
          type: REQUEST_RESULT,
          payload: { message: `Error status: ${error.response.status}` },
        });
      }
    });
}

export function fetchBackups(customerName, machineID, { setSubmitting }) {
  return dispatch => axios.get(`${API_URL}/backups/${machineID}/?customerName=${encodeURIComponent(customerName)}`, dispatch(headerConfig()))
    .then(response => {
        return dispatch({
          type: FETCH_USER_BACKUPS,
          payload: response.data,
        });
      },
    )
    .catch(error => {
      dispatch({
        type: REQUEST_RESULT,
        payload: {
          message: error && error.response
            ? error.response.status === 404
              ? `Error ${error.response.status} - not found`
              : error.response.data
            : 'An error occurred',
        },
      });
    }).finally(() => setSubmitting(false));
}

export function downloadBackupFile(machineID, fileName, date, setLoadingProgress) {
  setLoadingProgress(0);
  return dispatch => axios.post(`${API_URL}/backups/download/${machineID}/?fileName=${fileName}`, {}, {
    ...dispatch(headerConfig()),
    onDownloadProgress: (progressEvent) => {
      const progress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
      setLoadingProgress && setLoadingProgress(progress);
    },
  }).then(response => {
    const blob = new Blob([response.data], { type: 'application/octet-stream' });
    const downloadFileName = `${fileName}`;
    FileSaver.saveAs(blob, downloadFileName);
  }).catch(error => {
    dispatch({
      type: REQUEST_RESULT,
      payload: {
        message: error && error.response
          ? error.response.status === 404
            ? `${error.response.status} - File not found`
            : error.response.data
          : 'An error occurred',
      },
    });
  }).finally(() => {
    setLoadingProgress(null);
  });
}

export function clearZReports() {
  return dispatch => {
    dispatch({
      type: FETCH_Z_REPORTS,
      payload: undefined,
    });
  };
}

export function clearTransactionReports() {
  return dispatch => {
    dispatch({
      type: FETCH_TRANSACTION_REPORTS,
      payload: undefined,
    });
  };
}

export function clearComparisonReports() {
  return dispatch => {
    dispatch({
      type: FETCH_COMPARISON_REPORTS,
      payload: undefined,
    });
  };
}

export function clearStandardReports() {
  return dispatch => {
    dispatch({
      type: FETCH_STANDARD_REPORTS,
      payload: undefined,
    });
  };
}

export function fetchInvoices() {
  return dispatch => axios.get(`${REPORTS_URL}/get-system-invoices`, dispatch(headerConfig()))
    .then(response => {
        return dispatch({
          type: FETCH_USER_INVOICES,
          payload: response.data.Items,
        });
      },
    )
    .catch(error => {
        dispatch({
          type: REQUEST_RESULT,
          payload: { message: error.response.data },
        });
      },
    );
}

export function downloadInvoice(setDownloadProgress, invoiceId, event) {
  event.stopPropagation();
  setDownloadProgress(0);
  return dispatch => axios.get(`${REPORTS_URL}/download-system-invoice/${invoiceId}`,
    {
      ...dispatch(headerConfig()),
      responseType: 'blob',
      onDownloadProgress: (progressEvent) => {
        const progress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
        setDownloadProgress(progress);
      },
    },
  )
    .then(response => {
        const invoiceFile = new File(
          [new Blob([response.data])],
          `Invoice_${invoiceId}.pdf`,
          { type: 'application/pdf' });
        FileSaver.saveAs(invoiceFile);
        analyticEvent('Invoice Download', 'PDF Download', 'Success', { invoiceId: invoiceId });
      },
    )
    .catch(() => {
      analyticEvent('Invoice Download', 'PDF Download', 'Failure', { invoiceId: invoiceId });
    });
}

export function downloadTransaction(setDownloadProgress, transactionID, isInvoice) {
  setDownloadProgress(0);
  const type = isInvoice ? 'invoice' : 'receipt';
  return dispatch => axios.get(`${API_URL}/transaction/download-transaction/${transactionID}`,
    {
      ...dispatch(headerConfig()),
      responseType: 'blob',
      onDownloadProgress: (progressEvent) => {
        const progress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
        setDownloadProgress && setDownloadProgress(progress);
      },
    },
  )
    .then(response => {
      const dateFile = formatTimestampAsDate(response.headers.date);
      const invoiceFile = new File(
        [new Blob([response.data])],
        `${type}__${dateFile}__${transactionID}.pdf`,
        { type: 'application/pdf' });
      FileSaver.saveAs(invoiceFile);
      analyticEvent('Transaction Download', 'PDF Download', 'Success', { transactionID: transactionID });
    })
    .catch(error => {
      analyticEvent('Transaction Download', 'PDF Download', 'Failure', { transactionID: transactionID });
        if (error && error.response.status === 404) {
          dispatch({
            type: REQUEST_RESULT,
            payload: { message: `${error.response.status} - File not found` },
          });
        }
      },
    );
}

export function fetchTransactionReports(values, form) {
  const [dateFrom, dateTo, type, machines] = [formatTimestampAsDate(values.dateFrom), formatTimestampAsDate(values.dateTo), values.transactionType, values.machineIDs];
  const selectedMachines = machines.filter(Boolean).map(id => `&machineId=${id}`).join('');
  const [startTime, endTime] = [encodeURIComponent('T00:00:00'), encodeURIComponent('T23:59:59')];
  
  return dispatch => axios.get(`${API_URL}/transaction/?offset=0&fetch=200&startDateTime=${dateFrom}${startTime}&endDateTime=${dateTo}${endTime}&transactionType=${type}${selectedMachines}`, dispatch(headerConfig()))
    .then(response => {
        form.setSubmitting(false);
        dispatch({
          type: FETCH_TRANSACTION_REPORTS,
          payload: response.data.Items,
        });
        dispatch({
          type: FETCH_TRANSACTION_REPORTS_TOTAL,
          payload: response.data.ItemCountTotal,
        });
      },
    )
    .catch(error => {
        form.setSubmitting(false);
        dispatch({
          type: REQUEST_RESULT,
          payload: { message: error.response.data },
        });
      },
    );
}

export function fetchTransactionReportsOffset(values, form, setIsButtonDisabled, offset = 0) {
  const [dateFrom, dateTo, type, machines] = [formatTimestampAsDate(values.dateFrom), formatTimestampAsDate(values.dateTo), values.transactionType, values.machineIDs];
  const selectedMachines = machines.filter(Boolean).map(id => `&machineId=${id}`).join('');
  const [startTime, endTime] = [encodeURIComponent('T00:00:00'), encodeURIComponent('T23:59:59')];
  
  return dispatch => axios.get(`${API_URL}/transaction/?offset=${offset}&fetch=200&startDateTime=${dateFrom}${startTime}&endDateTime=${dateTo}${endTime}&transactionType=${type}${selectedMachines}`, dispatch(headerConfig()))
    .then(response => {
        form.setSubmitting(false);
        setIsButtonDisabled(false);
        return dispatch({
          type: FETCH_TRANSACTION_REPORTS_OFFSET,
          payload: response.data.Items,
        });
      },
    )
    .catch(error => {
        form.setSubmitting(false);
        if (error.response.status === 400) {
          dispatch({
            type: REQUEST_RESULT,
            payload: { message: error.response.data },
          });
        }
      },
    );
}